/**
 * 借币市场快照 API（python/loan-info）
 */

import { API_CONFIG } from '@/config/api';
import { apiFetch } from '@/lib/apiFetch';

const BASE = API_CONFIG.LOAN_INFO_PATH_PREFIX.replace(/\/?$/, '');

export const LOAN_EXCHANGES = [
  { value: 'binance', label: 'Binance' },
  { value: 'okx', label: 'OKX' },
  { value: 'bybit', label: 'Bybit' },
  { value: 'gateio', label: 'Gateio' },
] as const;

export type LoanExchangeValue = (typeof LOAN_EXCHANGES)[number]['value'];

export interface LoanSnapshotRow {
  ts: string;
  exchange: string;
  currency: string;
  vip_level: string;
  hourly_borrow_rate: number | null;
  max_borrow_amount: number | null;
  borrowable: boolean | null;
  note: string | null;
}

export interface LoanCurrentResponse {
  count: number;
  lookback_hours: number;
  data: LoanSnapshotRow[];
}

export interface LoanHistoryResponse {
  exchange: string;
  currency: string;
  vip_level: string | null;
  start_time: string;
  end_time: string;
  count: number;
  data: LoanSnapshotRow[];
}

export interface LoanCurrentParams {
  exchange: string;
  currency: string;
  vip_level?: string;
  lookback_hours?: number;
}

export async function getLoanRatesCurrent(params: LoanCurrentParams): Promise<LoanCurrentResponse> {
  const q = new URLSearchParams();
  q.set('exchange', params.exchange.toLowerCase());
  q.set('currency', params.currency.trim().toUpperCase());
  if (params.vip_level?.trim()) q.set('vip_level', params.vip_level.trim());
  if (params.lookback_hours != null) q.set('lookback_hours', String(params.lookback_hours));

  const res = await apiFetch(`${BASE}/loan-rates/current?${q.toString()}`);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json() as Promise<LoanCurrentResponse>;
}

export interface LoanHistoryParams {
  exchange: string;
  currency: string;
  start_time: string;
  end_time: string;
  vip_level?: string;
  limit?: number;
}

export async function getLoanRatesHistory(params: LoanHistoryParams): Promise<LoanHistoryResponse> {
  const q = new URLSearchParams();
  q.set('exchange', params.exchange.toLowerCase());
  q.set('currency', params.currency.trim().toUpperCase());
  q.set('start_time', params.start_time);
  q.set('end_time', params.end_time);
  if (params.vip_level?.trim()) q.set('vip_level', params.vip_level.trim());
  if (params.limit != null) q.set('limit', String(params.limit));

  const res = await apiFetch(`${BASE}/loan-rates/history?${q.toString()}`);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json() as Promise<LoanHistoryResponse>;
}

/** 解析 ClickHouse / API 返回的时间字段为毫秒时间戳 */
export function parseLoanTs(ts: string): number {
  const s = String(ts).trim();
  const normalized = s.includes('T') ? s : s.replace(' ', 'T');
  const d = new Date(normalized);
  const ms = d.getTime();
  return Number.isFinite(ms) ? ms : 0;
}
